/**
 * AP bills advanced-filter document — a versioned value the web filter panel
 * (`APBillFilterPanel`) edits and evaluates CLIENT-SIDE over the complete AP
 * bill index. One schema + ONE evaluator, both in @releve/shared (the
 * places/Impact template: `placesFilters.ts`, `sustainabilityFilters.ts`),
 * shared by:
 *  - the web filter panel (live evaluation as controls change),
 *  - any future server consumer (reuse `applyAPBillFilterDocument` — never
 *    build SQL WHERE clauses from the document).
 *
 * The AR twin (`arInvoiceFilters.ts`) is deliberately identical in shape:
 * receivables and payables sit side by side under Accounting and are worked
 * the same way, so what the two documents genuinely share lives in
 * `filterDocumentPrimitives.ts` and cannot drift. The two real divergences are
 * the vocabulary itself — AP has the approval/payment status PAIR where AR has
 * one status column, and AP carries `wkr_summary`, which AR can never have
 * (`nl_wkr_sync_from_lines` RAISEs on AR source types).
 *
 * The document is AD HOC — it lives in component state for the session.
 *
 * ── THIS IS A MOVE, NOT AN EXPANSION ────────────────────────────────────────
 * v1's vocabulary is EXACTLY the eleven columns the grid used to mark
 * `filterable: true` (apBillGridColumns.tsx), so the conversion to the panel
 * is behaviour-preserving and reviewable. Amount ranges are deliberately
 * absent: net-new UI, not a port.
 *
 * Per docs/INDEX_GRID_PATTERN.md step 9b, a grid runs the panel OR filterable
 * columns, never both: two filter states means the panel silently shrinks the
 * header option lists, each has its own Clear, and the badge counts only one
 * of them. The AP grid ends with ZERO filterable columns.
 *
 * v1 fields are `bill.<field>` over the MAPPED grid rows (`APBillGridRow`),
 * not the wire row — both statuses are null-normalized and the date columns
 * are reduced to local calendar days by the mapper, so evaluation runs AFTER
 * the mapper and BEFORE the grid shell (`hasUpstreamFilters`). The field
 * string carries its namespace, so a later vocabulary stays additive.
 */
import { z } from "zod";
import { makeDateConditionSchema, makeFilterDocumentSchema, makeStringConditionSchema, matchesDateOp, matchesStringOp, toLocalCalendarDay, } from "./filterDocumentPrimitives.js";
// ---------------------------------------------------------------------------
// Field vocabulary (v1) — the eleven ex-`filterable` columns
// ---------------------------------------------------------------------------
export const AP_BILL_FILTER_STRING_FIELDS = [
    "bill.payment_status",
    "bill.approval_status",
    "bill.payee_id",
    "bill.tax_summary",
    "bill.wkr_summary",
    "bill.project_id",
    "bill.ap_account_id",
    "bill.fund_id",
];
export const AP_BILL_FILTER_DATE_FIELDS = [
    "bill.invoice_date",
    "bill.due_date",
    "bill.record_created_at",
];
// ---------------------------------------------------------------------------
// Conditions
// ---------------------------------------------------------------------------
const stringConditionSchema = makeStringConditionSchema(AP_BILL_FILTER_STRING_FIELDS);
const dateConditionSchema = makeDateConditionSchema(AP_BILL_FILTER_DATE_FIELDS);
export const apBillFilterConditionSchema = z.union([
    stringConditionSchema,
    dateConditionSchema,
]);
// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------
export const apBillFilterDocumentSchema = makeFilterDocumentSchema(apBillFilterConditionSchema);
/** The empty document — "no filters active". */
export const EMPTY_AP_BILL_FILTER_DOCUMENT = {
    version: 1,
    match: "all",
    conditions: [],
};
/*
 * One accessor per field FAMILY, each an exhaustive switch over its
 * vocabulary (the reason `sustainabilityFilters` keeps them: a new vocabulary
 * entry is a `TS2366: Function lacks ending return statement` until it is
 * handled here, instead of compiling clean and filtering the wrong column).
 */
function stringValue(row, field) {
    switch (field) {
        case "bill.payment_status":
            return row.payment_status;
        case "bill.approval_status":
            return row.approval_status;
        case "bill.payee_id":
            return row.payee_id ?? "";
        case "bill.tax_summary":
            // Already `''` (never null) on a bill with no lines.
            return row.tax_summary;
        case "bill.wkr_summary":
            return row.wkr_summary ?? "";
        case "bill.project_id":
            return row.project_id ?? "";
        case "bill.ap_account_id":
            return row.ap_account_id ?? "";
        case "bill.fund_id":
            return row.fund_id ?? "";
    }
}
/**
 * The LOCAL calendar day (`yyyy-MM-dd`) to compare — verbatim for the two
 * mapper-normalized columns, reduced from the instant for the raw
 * `record_created_at` timestamp. Compared as STRINGS (lexicographic order is
 * date order for this shape); no `new Date()` round-trip on a date-only value.
 */
function dateValue(row, field) {
    switch (field) {
        case "bill.invoice_date":
            return row.invoice_date;
        case "bill.due_date":
            return row.due_date;
        case "bill.record_created_at":
            return toLocalCalendarDay(row.record_created_at);
    }
}
// The two condition families have disjoint op sets, so op alone narrows.
function matchesCondition(row, c) {
    switch (c.op) {
        case "in":
        case "is_empty":
            return matchesStringOp(c.op, stringValue(row, c.field), c.values, c.include_empty);
        case "date_gte":
        case "date_lte":
        case "date_between":
            return matchesDateOp(c.op, dateValue(row, c.field), c.min, c.max);
    }
}
/**
 * Apply a filter document to mapped AP bill rows (AND of all conditions — v1
 * `match` is 'all' only). An empty document returns the input array unchanged
 * (identity — callers can rely on reference equality to skip re-renders).
 */
export function applyAPBillFilterDocument(rows, doc) {
    if (doc.conditions.length === 0)
        return rows;
    return rows.filter((row) => doc.conditions.every((c) => matchesCondition(row, c)));
}
/** Count of active conditions — the filter button's badge. */
export function countActiveAPBillConditions(doc) {
    return doc.conditions.length;
}
//# sourceMappingURL=apBillFilters.js.map